import { CustomerProgress } from '../types';

export const ValidationUtils = {
  isValidCustomerId: (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    return /^[a-zA-Z0-9_-]+$/.test(id) && id.length >= 1 && id.length <= 50;
  },

  isValidTaskId: (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    return /^[a-zA-Z0-9_-]+$/.test(id) && id.length >= 1 && id.length <= 50;
  },

  isValidTaskStatus: (status: string): status is CustomerProgress['status'] => {
    return ['pending', 'in_progress', 'completed', 'skipped'].includes(status);
  },

  sanitizeInput: (input: string): string => {
    if (!input || typeof input !== 'string') return '';
    return input.replace(/[<>"'&]/g, '').trim();
  },

  validateEmail: (email: string): boolean => {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  },

  isValidSegment: (segment: string): boolean => {
    return ['small_business', 'enterprise', 'e-commerce'].includes(segment);
  },

  isValidOnboardingStatus: (status: string): boolean => {
    return ['not_started', 'in_progress', 'completed'].includes(status);
  },

  isValidTaskType: (type: string): boolean => {
    return ['required', 'recommended', 'optional'].includes(type);
  },

  validateSpreadsheetId: (id: string): boolean => {
    if (!id || typeof id !== 'string') return false;
    // Google Sheets ID pattern: alphanumeric, hyphens, underscores
    return /^[a-zA-Z0-9_-]{44}$/.test(id);
  },

  sanitizeHtml: (input: string): string => {
    if (!input || typeof input !== 'string') return '';
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  },

  sanitizeRichText: (input: string): string => {
    if (!input || typeof input !== 'string') return '';
    
    // Remove script tags and their content completely
    let sanitized = input.replace(/<script[^>]*>.*?<\/script>/gis, '');
    
    // Remove dangerous tags
    sanitized = sanitized.replace(/<(iframe|object|embed|form|input|button|link|meta)[^>]*>/gi, '');
    
    // Remove javascript: urls
    sanitized = sanitized.replace(/javascript:/gi, '');
    
    // Remove on* event handlers
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    
    // Clean up any remaining dangerous protocols
    sanitized = sanitized.replace(/href\s*=\s*["'](?!https?:\/\/|mailto:|#)[^"']*["']/gi, '');
    
    // Remove background color styles that conflict with the design
    sanitized = sanitized.replace(/background-color\s*:\s*[^;'"]+[;'"]/gi, '');
    sanitized = sanitized.replace(/background\s*:\s*[^;'"]*rgb\([^)]*\)[^;'"]*[;'"]/gi, '');
    sanitized = sanitized.replace(/background\s*:\s*[^;'"]*#[a-fA-F0-9]{3,6}[^;'"]*[;'"]/gi, '');
    
    // Clean up empty style attributes
    sanitized = sanitized.replace(/style\s*=\s*["']\s*["']/gi, '');
    
    return sanitized;
  },

  isValidDate: (dateString: string): boolean => {
    if (!dateString || typeof dateString !== 'string') return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  },

  isValidScore: (score: number): boolean => {
    return typeof score === 'number' && !isNaN(score) && score >= 0 && score <= 100;
  }
};

export class RateLimiter {
  private requests = new Map<string, number[]>();
  private readonly maxRequests: number;
  private readonly windowMs: number;

  constructor(maxRequests: number = 100, windowMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  async checkRateLimit(clientId: string = 'default'): Promise<void> {
    const now = Date.now();
    const requests = this.requests.get(clientId) ?? [];
    
    // Remove requests older than the window
    const recentRequests = requests.filter(time => now - time < this.windowMs);
    
    if (recentRequests.length >= this.maxRequests) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    recentRequests.push(now);
    this.requests.set(clientId, recentRequests);
  }

  getRequestCount(clientId: string = 'default'): number {
    const now = Date.now();
    const requests = this.requests.get(clientId) ?? [];
    return requests.filter(time => now - time < this.windowMs).length;
  }

  reset(clientId?: string): void {
    if (clientId) {
      this.requests.delete(clientId);
    } else {
      this.requests.clear();
    }
  }
}